import { ManageFootballFixture } from "../../../commons/models/football/admin/manage-football-fixture";
import { FootballKnockoutTree } from "../../../commons/models/football/football-knockout-tree";
import { ManageFootballMatchPlayersStatistics } from "../../../commons/models/football/admin/manage-football-match-players-statistics";
import { ManageFootballMatchPossession } from "../../../commons/models/football/admin/manage-football-match-possession";
import { ManageFootballSeasonFixtureGroup } from "../../../commons/models/football/admin/manage-football-season-fixture-group";
import { ManageFootballSeasonTeamGroup } from "../../../commons/models/football/admin/manage-football-season-team-group";
import { FootballCommentary } from "../../../commons/models/football/football-commentary";
import { FootballMatchDetail } from "../../../commons/models/football/football-match-detail";
import { FootballMatchTeams } from "../../../commons/models/football/football-match-teams";
import { FootballSeason } from "../../../commons/models/football/football-season";
import { IFootballPlayerLineup } from "../../../commons/models/football/interface/i-football-player-lineup";
import { AbstractBaseService } from "../../../ensue-react-system/utilities/base-service";
import { EnsueCollection } from "../../../ensue-react-system/utilities/ensue-collection";
import { PaginatedEnsueCollection } from "../../../ensue-react-system/utilities/paginated-ensue-collection";
import { ISeasonTeamsInfo } from "./manage-football-competition-service";
import { ManageFootballMatchPlayerPassStatistics } from "../../../commons/models/football/admin/manage-football-match-players-pass-statistics";
import { FootballMatchStat } from "../../../commons/models/football/football-match-stats";
import { ManageFootballTeam } from "../../../commons/models/football/admin/manage-football-team";
import { FootballTeamPlayer } from "../../../commons/models/football/football-team-player";
import { ManageFootballReferee } from "../../../commons/models/football/admin/manage-football-referee";
import { Stadium } from "../../../commons/models/stadium";

export interface IMatchPlayersStatisticDTO {
  key: string;
  value: number;
  gameTime: number;
  gameExtraTime: number | null;
}
export interface IMatchPenaltyDTO {
  playerId: number;
  goalScored: boolean;
  penaltyNumber?: number;
}
export interface IMatchPlayerPassStatisticDTO {
  passes: number;
  passSucceeded: number;
  gameTime: number;
  gameExtraTime: number | null;
}
export interface IMatchPossessionDTO {
  homePossession: number;
  gameTime: number;
  gameExtraTime: number | null;
}
export interface IMatchCommentaryData {
  playerId: number;
  supportingPlayerId: number;
  eventType: string;
  gameTime: number;
  gameExtraTime: number | null;
  metadata: string;
}
export interface IGroupInfo {
  name: string;
}
export interface IKnockoutDTO {
  noOfMatches: number;
  thirdPlace: boolean;
}
export interface IMatchStartInfo {
  startAt: string;
}

export interface IMatchStateInfo {
  startAt?: string;
  state: number;
}
export interface IInjuryTimeData {
  addedTime: number;
}

export interface IFootballMatchEditDTO {
  stadiumId: number | null;
  scheduledAt: string;
  referees: IReferee[];
  gameplayDuration: number;
}
export interface IFootballMatchDTO {
  homeTeamId: number | null;
  awayTeamId: number | null;
  stadiumId: number | null;
  scheduledAt: string;
  referees: IReferee[];
  gameplayDuration: number;
  homeTeamOutcome?: {
    modelType: string;
    modelId: number;
    position: number;
  } | null;
  awayTeamOutcome?: {
    modelType: string;
    modelId: number;
    position: number;
  } | null;
}
export interface IMatchTeamLineupInfo {
  lineUps: string;
  players: IFootballPlayerLineup[];
}

interface IReferee {
  id: number;
  role: string;
}

export class ManageFootballSeasonService extends AbstractBaseService<FootballSeason> {
  protected __model: FootballSeason = new FootballSeason();

  public getResourceName(): string {
    return "manage/football/seasons";
  }

  public getSeasonTeamGroups(
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<ManageFootballSeasonTeamGroup>> {
    const seasonGroupModel = new ManageFootballSeasonTeamGroup();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/team-groups", options)
      .then((obj) => seasonGroupModel.createFromCollection(obj) as EnsueCollection<ManageFootballSeasonTeamGroup>);
  }

  public addSeasonTeamGroup(
    seasonId: number,
    groupInfo: IGroupInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.post(this.__url + "/" + seasonId + "/team-groups", groupInfo, options);
  }

  public editSeasonTeamGroup(
    seasonId: number,
    groupId: number,
    groupInfo: IGroupInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.patch(this.__url + "/" + seasonId + "/team-groups/" + groupId, groupInfo, options);
  }

  public deleteSeasonTeamGroup(
    seasonId: number,
    groupId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.delete(this.__url + "/" + seasonId + "/team-groups/" + groupId, options);
  }

  public addSeasonTeamGroupTeams(
    seasonId: number,
    groupId: number,
    teams: ISeasonTeamsInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.post(this.__url + "/" + seasonId + "/team-groups/" + groupId + "/teams", teams, options);
  }

  public removeSeasonTeamGroupTeam(
    seasonId: number,
    groupId: number,
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.delete(this.__url + "/" + seasonId + "/team-groups/" + groupId + "/teams/" + teamId, options);
  }

  public getSeasonFixtureGroups(
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<ManageFootballSeasonFixtureGroup>> {
    const seasonGroupModel = new ManageFootballSeasonFixtureGroup();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/fixture-groups", options)
      .then((obj) => seasonGroupModel.createFromCollection(obj) as EnsueCollection<ManageFootballSeasonFixtureGroup>);
  }

  public addSeasonFixtureGroup(
    seasonId: number,
    groupInfo: IGroupInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.post(this.__url + "/" + seasonId + "/fixture-groups", groupInfo, options);
  }

  public editSeasonFixtureGroup(
    seasonId: number,
    groupId: number,
    groupInfo: IGroupInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.patch(this.__url + "/" + seasonId + "/fixture-groups/" + groupId, groupInfo, options);
  }

  public deleteSeasonFixtureGroup(
    seasonId: number,
    groupId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.delete(this.__url + "/" + seasonId + "/fixture-groups/" + groupId, options);
  }

  public getSeasonFixtureGroupFixtureList(
    seasonId: number,
    groupId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<ManageFootballFixture>> {
    const seasonGroupFixtureModel = new ManageFootballFixture();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/fixture-groups/" + groupId + "/fixtures", options)
      .then((obj) => seasonGroupFixtureModel.createFromCollection(obj) as EnsueCollection<ManageFootballFixture>);
  }

  public addSeasonFixtureGroupFixture(
    seasonId: number,
    groupId: number,
    matchInfo: IFootballMatchDTO,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.post(
      this.__url + "/" + seasonId + "/fixture-groups/" + groupId + "/fixtures",
      matchInfo,
      options
    );
  }

  public editSeasonFixtureGroupFixture(
    seasonId: number,
    groupId: number,
    fixtureId: number,
    matchInfo: IFootballMatchDTO,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.patch(
      this.__url + "/" + seasonId + "/fixture-groups/" + groupId + "/fixtures/" + fixtureId,
      matchInfo,
      options
    );
  }

  public deleteSeasonFixtureGroupFixture(
    seasonId: number,
    groupId: number,
    fixtureId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.delete(
      this.__url + "/" + seasonId + "/fixture-groups/" + groupId + "/fixtures/" + fixtureId,
      options
    );
  }

  public getSeasonMatchList(
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<FootballMatchDetail>> {
    const seasonMatchListModel = new FootballMatchDetail();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/matches", options)
      .then((obj) => seasonMatchListModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballMatchDetail>);
  }

  public async getSeasonMatch(
    seasonId: number,
    matchId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ): Promise<FootballMatchDetail> {
    const seasonMatchModel = new FootballMatchDetail();
    let options = this.createHttpParamObject(queryParams);
    const obj = await this.__client.get(this.__url + "/" + seasonId + "/matches/" + matchId, options);
    if (obj?.homeTeam) {
      obj.homeTeam = new ManageFootballTeam().create(obj.homeTeam) as ManageFootballTeam;
    }
    if (obj?.awayTeam) {
      obj.awayTeam = new ManageFootballTeam().create(obj.awayTeam) as ManageFootballTeam;
    }
    return seasonMatchModel.create(obj) as FootballMatchDetail;
  }

  public getSeasonMatchTeams(
    seasonId: number,
    matchId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ): Promise<FootballMatchTeams> {
    const seasonMatchTeamsModel = new FootballMatchTeams();
    let options = this.createHttpParamObject(queryParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/matches/" + matchId + "/teams", options)
      .then((obj) => seasonMatchTeamsModel.create(obj) as FootballMatchTeams);
  }

  public getSeasonMatchTeamPlayers(
    seasonId: number,
    matchId: number,
    teamId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<FootballTeamPlayer>> {
    const seasonMatchTeamPlayerModel = new FootballTeamPlayer();
    let options = this.createHttpParamObject(queryParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/matches/" + matchId + "/teams/" + teamId + "/eligible-players", options)
      .then((obj) => seasonMatchTeamPlayerModel.createFromCollection(obj) as EnsueCollection<FootballTeamPlayer>);
  }

  public getSeasonReferees(
    seasonId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<ManageFootballReferee>> {
    const seasonMatchRefereeModel = new ManageFootballReferee();
    let options = this.createHttpParamObject(queryParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/available-referees", options)
      .then(
        (obj) => seasonMatchRefereeModel.createFromCollection(obj) as PaginatedEnsueCollection<ManageFootballReferee>
      );
  }

  public getSeasonStadiums(
    seasonId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<Stadium>> {
    const seasonMatchTeamsModel = new Stadium();
    let options = this.createHttpParamObject(queryParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/available-stadiums", options)
      .then((obj) => seasonMatchTeamsModel.createFromCollection(obj) as PaginatedEnsueCollection<Stadium>);
  }

  public editSeasonMatch(
    seasonId: number,
    matchId: number,
    matchEditInfo: IFootballMatchEditDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(this.__url + "/" + seasonId + "/matches/" + matchId, matchEditInfo, options);
  }

  public addEditTeamFormation(
    seasonId: number,
    matchId: number,
    teamId: number,
    teamLineupInfo: IMatchTeamLineupInfo,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.post(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/teams/" + teamId + "/players",
      teamLineupInfo,
      options
    );
  }

  public startSeasonMatch(
    seasonId: number,
    matchId: number,
    matchStartInfo: IMatchStartInfo,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(this.__url + "/" + seasonId + "/matches/" + matchId + "/start", matchStartInfo, options);
  }

  public endSeasonMatch(seasonId: number, matchId: number, queryParams?: { [key: string]: string | number | boolean }) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(this.__url + "/" + seasonId + "/matches/" + matchId + "/end", undefined, options);
  }

  public changeSeasonMatchState(
    seasonId: number,
    matchId: number,
    stateId: number,
    stateData: IMatchStateInfo,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(this.__url + `/${seasonId}/matches/${matchId}/states/${stateId}`, stateData, options);
  }

  public addSeasonMatchInjuryTime(
    seasonId: number,
    matchId: number,
    stateId: number,
    injuryTimeData: IInjuryTimeData,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(
      this.__url + `/${seasonId}/matches/${matchId}/states/${stateId}/add-time`,
      injuryTimeData,
      options
    );
  }

  public getSeasonMatchCommentaryList(
    seasonId: number,
    matchId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<FootballCommentary>> {
    const seasonMatchCommentaryListModel = new FootballCommentary();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/matches/" + matchId + "/commentaries", options)
      .then(
        (obj) =>
          seasonMatchCommentaryListModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballCommentary>
      );
  }

  public addSeasonMatchCommentary(
    seasonId: number,
    matchId: number,
    commentaryData: IMatchCommentaryData,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.post(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/commentaries",
      commentaryData,
      options
    );
  }

  public editSeasonMatchCommentary(
    seasonId: number,
    matchId: number,
    commentaryId: number,
    commentaryData: IMatchCommentaryData,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/commentaries/" + commentaryId,
      commentaryData,
      options
    );
  }

  public deleteSeasonMatchCommentary(
    seasonId: number,
    matchId: number,
    commentaryId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.delete(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/commentaries/" + commentaryId,
      options
    );
  }

  public getSeasonMatchPossessionList(
    seasonId: number,
    matchId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<ManageFootballMatchPossession>> {
    const seasonMatchPossessionListModel = new ManageFootballMatchPossession();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/matches/" + matchId + "/possessions", options)
      .then(
        (obj) =>
          seasonMatchPossessionListModel.createFromCollection(
            obj
          ) as PaginatedEnsueCollection<ManageFootballMatchPossession>
      );
  }

  public addSeasonMatchPossession(
    seasonId: number,
    matchId: number,
    possessionData: IMatchPossessionDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.post(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/possessions",
      possessionData,
      options
    );
  }

  public editSeasonMatchPossession(
    seasonId: number,
    matchId: number,
    possessionId: number,
    possessionData: IMatchPossessionDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/possessions/" + possessionId,
      possessionData,
      options
    );
  }

  public deleteSeasonMatchPossession(
    seasonId: number,
    matchId: number,
    possessionId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.delete(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/possessions/" + possessionId,
      options
    );
  }

  public getMatchStatistics(
    seasonId: number,
    matchId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ): Promise<FootballMatchStat> {
    const statisticModel = new FootballMatchStat();
    let options = this.createHttpParamObject(queryParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/matches/" + matchId + "/statistics", options)
      .then((obj) => statisticModel.create(obj) as FootballMatchStat);
  }

  public getSeasonMatchPlayerStatistics(
    seasonId: number,
    matchId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<ManageFootballMatchPlayersStatistics>> {
    const seasonMatchPlayerStatisticsModel = new ManageFootballMatchPlayersStatistics();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/matches/" + matchId + "/players-statistics", options)
      .then(
        (obj) =>
          seasonMatchPlayerStatisticsModel.createFromCollection(
            obj
          ) as PaginatedEnsueCollection<ManageFootballMatchPlayersStatistics>
      );
  }

  public addSeasonMatchPlayerStatistic(
    seasonId: number,
    matchId: number,
    playerId: number,
    statisticData: IMatchPlayersStatisticDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.post(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/players/" + playerId + "/statistics",
      statisticData,
      options
    );
  }

  public editSeasonMatchPlayerStatistic(
    seasonId: number,
    matchId: number,
    playerId: number,
    statisticId: number,
    statisticData: IMatchPlayersStatisticDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/players/" + playerId + "/statistics/" + statisticId,
      statisticData,
      options
    );
  }

  public deleteSeasonMatchPlayerStatistic(
    seasonId: number,
    matchId: number,
    playerId: number,
    statisticId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.delete(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/players/" + playerId + "/statistics/" + statisticId,
      options
    );
  }

  public getSeasonMatchPlayerPassStatistics(
    seasonId: number,
    matchId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<ManageFootballMatchPlayerPassStatistics>> {
    const seasonMatchPlayerPassStatisticsModel = new ManageFootballMatchPlayerPassStatistics();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + seasonId + "/matches/" + matchId + "/players-pass-statistics", options)
      .then(
        (obj) =>
          seasonMatchPlayerPassStatisticsModel.createFromCollection(
            obj
          ) as PaginatedEnsueCollection<ManageFootballMatchPlayerPassStatistics>
      );
  }

  public addSeasonMatchPlayerPassStatistic(
    seasonId: number,
    matchId: number,
    playerId: number,
    statisticData: IMatchPlayerPassStatisticDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.post(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/players/" + playerId + "/pass-statistics",
      statisticData,
      options
    );
  }

  public editSeasonMatchPlayerPassStatistic(
    seasonId: number,
    matchId: number,
    playerId: number,
    statisticData: IMatchPlayerPassStatisticDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/players/" + playerId + "/pass-statistics/",
      statisticData,
      options
    );
  }

  public deleteSeasonMatchPlayerPassStatistic(
    seasonId: number,
    matchId: number,
    playerId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.delete(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/players/" + playerId + "/pass-statistics/",
      options
    );
  }

  public getSeasonMatchPenaltyList(
    seasonId: number,
    matchId: number,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.get(this.__url + "/" + seasonId + "/matches/" + matchId + "/penalties", options);
  }

  public addSeasonMatchPenalty(
    seasonId: number,
    matchId: number,
    penaltyData: IMatchPenaltyDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.post(this.__url + "/" + seasonId + "/matches/" + matchId + "/penalties", penaltyData, options);
  }

  public editSeasonMatchPenalty(
    seasonId: number,
    matchId: number,
    penaltyId: number,
    penaltyData: IMatchPenaltyDTO,
    queryParams?: { [key: string]: string | number | boolean }
  ) {
    let options = this.createHttpParamObject(queryParams);
    return this.__client.patch(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/penalties/" + penaltyId,
      penaltyData,
      options
    );
  }

  public deleteSeasonMatchPenalty(
    seasonId: number,
    matchId: number,
    penaltyId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.delete(
      this.__url + "/" + seasonId + "/matches/" + matchId + "/penalties/" + penaltyId,
      options
    );
  }

  public getSeasonKnockoutList(
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<FootballKnockoutTree> {
    const seasonKnockoutListModel = new FootballKnockoutTree();
    const options = this.createHttpParamObject(urlParams);
    return this.__client.get(this.__url + "/" + seasonId + "/knockouts", options).then((obj) => {
      return seasonKnockoutListModel.create(obj) as FootballKnockoutTree;
    });
  }

  public createSeasonKnockout(
    seasonId: number,
    knockoutInfo: IKnockoutDTO,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.post(this.__url + "/" + seasonId + "/knockouts", knockoutInfo, options);
  }

  public editSeasonKnockout(
    seasonId: number,
    knockoutInfo: IKnockoutDTO,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.patch(this.__url + "/" + seasonId + "/knockouts", knockoutInfo, options);
  }

  public editSeasonKnockoutMatch(
    seasonId: number,
    matchId: number,
    knockoutMatchInfo: IFootballMatchDTO,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.patch(this.__url + "/" + seasonId + "/knockouts/" + matchId, knockoutMatchInfo, options);
  }
}
